import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Download, Loader2, Redo2, Undo2, Upload, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { useContentStudioApi } from '../../../../common/hooks/use-content-studio-api.hook';
import {
    AspectRatio,
    MainClip,
    VideoComposition,
    addAudioClip,
    addOverlayLayer,
    audioTracks,
    clipAtTime,
    createAudioClip,
    createColorClip,
    createMainClip,
    deleteItem,
    duplicateItem,
    findItem,
    insertMainClips,
    mainInsertIndexAt,
    mainTrack,
    quantizeToFrame,
    setAspect,
    splitItemAt,
    totalDuration,
    updateItem,
} from './composition';
import { buildImageLayer, buildPresetLayers, buildScrim, buildShape, TextPresetId, presetMeta, VideoBrand } from './presets';
import { TemplateId, TemplateMedia, applyTemplate } from './templates';
import { useCompositionHistory } from './use-composition-history';
import { usePlayback } from './use-playback';
import { DroppedMedia, EditorSelection, Timeline } from './Timeline';
import { PreviewStage } from './PreviewStage';
import { Inspector } from './Inspector';
import { MediaBin, ShapeKind } from './MediaBin';
import { probeVideo } from './media';
import { ExportProgress } from './export';
import { loadVideoBrand } from './brand';

export interface VideoExportState {
    status: 'running' | 'done' | 'error';
    progress?: Omit<ExportProgress, 'phase'> & { phase: ExportProgress['phase'] | 'upload' };
    url?: string;
    error?: string;
}

export interface VideoEditorModalProps {
    open: boolean;
    projectUuid: string;
    initialComposition: VideoComposition;
    /** Debounced copy of every change, for autosave. */
    onChange?: (comp: VideoComposition) => void;
    onClose: (comp: VideoComposition) => void;
    /** Render + upload. The caller owns the job so it survives the editor closing. */
    onExport: (comp: VideoComposition) => void;
    exportState?: VideoExportState | null;
    onCancelExport?: () => void;
    onDismissExport?: () => void;
}

const IMAGE_SECONDS = 3;
const MAX_VIDEO_SECONDS = 8;

/**
 * The timeline editor: media bin, preview, inspector, timeline.
 *
 * It only edits a composition. Rendering is `renderFrame` (shared with the
 * exporter), and export + upload are the caller's, so a vendor can close the
 * editor while a long export finishes on the canvas node.
 */
export const VideoEditorModal: React.FC<VideoEditorModalProps> = (props) => {
    const { open, initialComposition, onClose } = props;
    const historyRef = useRef<VideoComposition>(initialComposition);
    return (
        <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(historyRef.current); }}>
            <DialogContent
                showCloseButton={false}
                onEscapeKeyDown={(e) => e.preventDefault()}
                onOpenAutoFocus={(e) => e.preventDefault()}
                onPointerDownOutside={(e) => e.preventDefault()}
                className="flex h-[96vh] w-[98vw] max-w-none flex-col gap-0 overflow-hidden bg-background p-0 sm:max-w-none"
                data-ve-modal
            >
                {open && <EditorBody {...props} compRef={historyRef} />}
            </DialogContent>
        </Dialog>
    );
};

const EditorBody: React.FC<VideoEditorModalProps & { compRef: React.MutableRefObject<VideoComposition> }> = ({
    projectUuid,
    initialComposition,
    onChange,
    onClose,
    onExport,
    exportState,
    onCancelExport,
    onDismissExport,
    compRef,
}) => {
    const { t } = useTranslation();
    const api = useContentStudioApi();
    const history = useCompositionHistory(initialComposition);
    const { comp, commit } = history;
    compRef.current = comp;
    const playback = usePlayback(comp);
    const [selection, setSelection] = useState<EditorSelection | null>(null);
    const [pps, setPps] = useState(60);
    const [snapping, setSnapping] = useState(true);
    const [safeArea, setSafeArea] = useState(false);
    const [brand, setBrand] = useState<VideoBrand>({});

    useEffect(() => {
        let alive = true;
        void loadVideoBrand(api.getBrandKit).then((b) => { if (alive) setBrand(b); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fit the timeline to the video on open.
    const fitted = useRef(false);
    useEffect(() => {
        if (fitted.current) return;
        fitted.current = true;
        const d = totalDuration(comp);
        if (d > 0) setPps(Math.max(12, Math.min(160, (window.innerWidth - 200) / (d + 2))));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Autosave, debounced. Keyed on the composition only: the callback's
    // identity changes with every canvas render and must not re-arm it.
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const first = useRef(true);
    useEffect(() => {
        if (first.current) { first.current = false; return undefined; }
        const id = setTimeout(() => onChangeRef.current?.(comp), 800);
        return () => clearTimeout(id);
    }, [comp]);

    // A deleted item cannot stay selected.
    useEffect(() => {
        if (selection && !findItem(comp, selection.id)) setSelection(null);
    }, [comp, selection]);

    const time = playback.time;

    // ─── actions ────────────────────────────────────────────────────

    const addMedia = useCallback(async (m: DroppedMedia, atIndex?: number) => {
        if (m.kind === 'audio') return;
        let sourceDuration = m.durationSec;
        if (m.kind === 'video' && !sourceDuration) {
            const probed = await probeVideo(m.src);
            sourceDuration = probed?.duration;
        }
        const clip = createMainClip({
            kind: m.kind,
            src: m.src,
            assetUuid: m.assetUuid,
            thumbnailUrl: m.thumbnailUrl,
            sourceDuration,
            duration: m.kind === 'video' ? Math.min(sourceDuration ?? 5, MAX_VIDEO_SECONDS) : IMAGE_SECONDS,
            transitionIn: mainTrack(history.committed()).clips.length ? { type: 'crossfade', duration: 0.5 } : undefined,
        });
        commit((c) => insertMainClips(c, [clip], atIndex));
        setSelection({ kind: 'clip', id: clip.id });
    }, [commit, history]);

    const addColorCard = useCallback((color: string) => {
        const clip = createColorClip({
            color,
            duration: 2.5,
            transitionIn: mainTrack(history.committed()).clips.length ? { type: 'crossfade', duration: 0.5 } : undefined,
        });
        commit((c) => insertMainClips(c, [clip]));
        setSelection({ kind: 'clip', id: clip.id });
    }, [commit, history]);

    const addAudio = useCallback((m: DroppedMedia, at = 0) => {
        const total = totalDuration(history.committed());
        const room = total > at ? total - at : undefined;
        const duration = Math.max(1, Math.min(m.durationSec ?? room ?? 30, room ?? Infinity));
        const clip = createAudioClip({ src: m.src, name: m.name, start: at, duration, sourceDuration: m.durationSec });
        commit((c) => addAudioClip(c, clip));
        setSelection({ kind: 'audio', id: clip.id });
    }, [commit, history]);

    /** How long a new overlay should run: to the end of the clip under the playhead, at least 2 s. */
    const overlaySpan = useCallback((c: VideoComposition, at: number) => {
        const hit = clipAtTime(mainTrack(c).clips, at);
        const end = hit ? hit.clip.start + hit.clip.duration : at + 3;
        return Math.max(2, Math.min(4, end - at));
    }, []);

    const addPreset = useCallback((id: TextPresetId) => {
        const c = history.committed();
        const total = totalDuration(c);
        const at = id === 'end-card' ? Math.max(0, total) : Math.min(time, Math.max(0, total - 0.5));
        const duration = id === 'end-card' ? 2.6 : overlaySpan(c, at);
        const meta = presetMeta(id);
        const words = meta ? t(meta.sample.textKey) : '';
        const texts = id === 'end-card'
            ? { primary: brand.tagline || '', secondary: brand.website || '' }
            : id === 'eyebrow-headline' || id === 'lower-third'
            ? { primary: words, secondary: t('VIDEO_EDITOR.SAMPLE.EYEBROW') }
            : { primary: words };
        const layers = buildPresetLayers(id, { aspect: c.aspect, start: at, duration, brand, texts });
        commit((cc) => layers.reduce((acc, l) => addOverlayLayer(acc, l), cc));
        const firstText = layers.find((l) => l.kind === 'text') ?? layers[0];
        if (firstText) setSelection({ kind: 'layer', id: firstText.id });
        if (id === 'end-card') playback.seek(at + 1.2);
    }, [history, time, overlaySpan, brand, commit, t, playback]);

    const addShape = useCallback((shape: ShapeKind) => {
        const c = history.committed();
        const at = Math.min(time, Math.max(0, totalDuration(c) - 0.5));
        const duration = overlaySpan(c, at);
        const layer = shape === 'scrim-bottom' || shape === 'scrim-top'
            ? buildScrim(c.aspect, at, duration, shape === 'scrim-bottom' ? 'bottom' : 'top')
            : buildShape(c.aspect, at, duration, shape);
        // Scrims belong under the type: on the lowest overlay track when it has room.
        commit((cc) => addOverlayLayer(cc, layer));
        setSelection({ kind: 'layer', id: layer.id });
    }, [history, time, overlaySpan, commit]);

    const addLogo = useCallback(() => {
        if (!brand.logoUrl) return;
        const c = history.committed();
        const at = Math.min(time, Math.max(0, totalDuration(c) - 0.5));
        const layer = buildImageLayer(brand.logoUrl, at, overlaySpan(c, at));
        commit((cc) => addOverlayLayer(cc, { ...layer, width: c.width > c.height ? 0.14 : 0.26, y: 0.1 }));
        setSelection({ kind: 'layer', id: layer.id });
    }, [brand.logoUrl, history, time, overlaySpan, commit]);

    const applyTemplateId = useCallback((id: TemplateId) => {
        const c = history.committed();
        const seen = new Set<string>();
        const media: TemplateMedia[] = [];
        for (const clip of mainTrack(c).clips) {
            // Colour cards are not media a template can reuse.
            if (clip.kind === 'color' || seen.has(clip.src)) continue;
            seen.add(clip.src);
            media.push({ src: clip.src, kind: clip.kind, assetUuid: clip.assetUuid, thumbnailUrl: clip.thumbnailUrl, durationSec: clip.sourceDuration, title: clip.label });
        }
        const music = audioTracks(c).flatMap((tr) => tr.clips)[0];
        const next = applyTemplate(id, media, {
            aspect: c.aspect,
            fps: c.fps,
            brand,
            labels: { before: t('VIDEO_EDITOR.SAMPLE.BEFORE'), after: t('VIDEO_EDITOR.SAMPLE.AFTER') },
            music: music ? { src: music.src, name: music.name, durationSec: music.sourceDuration, volume: music.volume } : undefined,
        });
        commit(next);
        setSelection(null);
        playback.seek(0);
    }, [history, brand, commit, t, playback]);

    const templateSourceCount = useMemo(() => new Set(mainTrack(comp).clips.filter((c) => c.kind !== 'color').map((c) => c.src)).size, [comp]);

    const onDropMedia = useCallback((m: DroppedMedia, at: { time: number; track: any }) => {
        if (m.kind === 'audio' || at.track.kind === 'audio') {
            if (m.kind === 'audio') addAudio(m, at.time);
            return;
        }
        if (at.track.kind === 'overlay') {
            const c = history.committed();
            const layer = { ...buildImageLayer(m.src, at.time, overlaySpan(c, at.time), m.aspect), y: 0.5, width: 0.5 };
            commit((cc) => addOverlayLayer(cc, layer));
            setSelection({ kind: 'layer', id: layer.id });
            return;
        }
        void addMedia(m, mainInsertIndexAt(mainTrack(history.committed()).clips, at.time));
    }, [addAudio, addMedia, history, overlaySpan, commit]);

    const splitAtPlayhead = useCallback(() => {
        const c = history.committed();
        const at = quantizeToFrame(c, time);
        let id = selection && selection.kind !== 'transition' ? selection.id : null;
        if (id) {
            const f = findItem(c, id);
            if (!f || at <= f.item.start || at >= f.item.start + f.item.duration) id = null;
        }
        if (!id) id = clipAtTime(mainTrack(c).clips, at)?.clip.id ?? null;
        if (!id) return;
        const { comp: next, newId } = splitItemAt(c, id, at);
        if (newId) {
            commit(next);
            const f = findItem(next, newId);
            if (f) setSelection({ kind: f.track.kind === 'main' ? 'clip' : f.track.kind === 'audio' ? 'audio' : 'layer', id: newId });
        }
    }, [history, time, selection, commit]);

    const deleteSelection = useCallback(() => {
        if (!selection) return;
        if (selection.kind === 'transition') {
            commit((c) => updateItem<MainClip>(c, selection.id, { transitionIn: undefined }));
            setSelection({ kind: 'clip', id: selection.id });
            return;
        }
        commit((c) => deleteItem(c, selection.id));
        setSelection(null);
    }, [selection, commit]);

    const duplicateSelection = useCallback(() => {
        if (!selection || selection.kind === 'transition') return;
        const { comp: next, newId } = duplicateItem(history.committed(), selection.id);
        if (!newId) return;
        commit(next);
        setSelection({ ...selection, id: newId });
    }, [selection, history, commit]);

    const changeAspect = useCallback((a: AspectRatio) => commit((c) => setAspect(c, a)), [commit]);

    // ─── keyboard ───────────────────────────────────────────────────

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const el = document.activeElement as HTMLElement | null;
            const tag = el?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable) return;
            const mod = e.metaKey || e.ctrlKey;
            const frame = 1 / comp.fps;
            if (e.code === 'Space') {
                e.preventDefault();
                playback.toggle();
            } else if (mod && e.key.toLowerCase() === 'z') {
                e.preventDefault();
                if (e.shiftKey) history.redo();
                else history.undo();
            } else if (mod && e.key.toLowerCase() === 'y') {
                e.preventDefault();
                history.redo();
            } else if (mod && e.key.toLowerCase() === 'd') {
                e.preventDefault();
                duplicateSelection();
            } else if (!mod && e.key.toLowerCase() === 's') {
                e.preventDefault();
                splitAtPlayhead();
            } else if (e.key === 'Delete' || e.key === 'Backspace') {
                e.preventDefault();
                deleteSelection();
            } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                e.preventDefault();
                const step = e.shiftKey ? 1 : frame;
                playback.seek(quantizeToFrame(comp, playback.time + (e.key === 'ArrowLeft' ? -step : step)));
            } else if (e.key === 'Home') {
                playback.seek(0);
            } else if (e.key === 'End') {
                playback.seek(totalDuration(comp));
            } else if (e.key === 'Escape') {
                setSelection(null);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [comp, playback, history, duplicateSelection, splitAtPlayhead, deleteSelection]);

    const exporting = exportState?.status === 'running';
    const canExport = totalDuration(comp) > 0 && !exporting;

    return (
        <div className="relative flex h-full min-h-0 flex-col">
            {/* Header */}
            <div className="flex h-12 shrink-0 items-center gap-2 border-b px-4">
                <DialogTitle className="font-heading text-sm font-medium tracking-tight text-foreground">
                    {t('VIDEO_EDITOR.TITLE')}
                </DialogTitle>
                <span className="text-xs tabular-nums text-muted-foreground">
                    {comp.width}×{comp.height} · {comp.fps} fps
                </span>
                <div className="ms-auto flex items-center gap-1.5">
                    <Button type="button" variant="ghost" size="icon" onClick={history.undo} disabled={!history.canUndo} aria-label={t('VIDEO_EDITOR.UNDO')} title={t('VIDEO_EDITOR.UNDO')}>
                        <Undo2 />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" onClick={history.redo} disabled={!history.canRedo} aria-label={t('VIDEO_EDITOR.REDO')} title={t('VIDEO_EDITOR.REDO')}>
                        <Redo2 />
                    </Button>
                    <div className="mx-1 h-5 w-px bg-border" />
                    <Button type="button" variant="outline" onClick={() => onClose(comp)}>
                        {t('VIDEO_EDITOR.DONE')}
                    </Button>
                    <Button type="button" onClick={() => onExport(comp)} disabled={!canExport} data-ve-export>
                        {exporting ? <Loader2 className="animate-spin" /> : <Upload />}
                        {t('VIDEO_EDITOR.EXPORT')}
                    </Button>
                    <Button type="button" variant="ghost" size="icon" onClick={() => onClose(comp)} aria-label={t('VIDEO_EDITOR.CLOSE')}>
                        <X />
                    </Button>
                </div>
            </div>

            {/* Workspace */}
            <div className="flex min-h-0 flex-1">
                <div className="w-[320px] shrink-0">
                    <MediaBin
                        projectUuid={projectUuid}
                        brand={brand}
                        videoDuration={totalDuration(comp)}
                        onAddMedia={(m) => void addMedia(m)}
                        onAddPreset={addPreset}
                        onAddShape={addShape}
                        onAddLogo={addLogo}
                        onAddAudio={(m) => addAudio(m, 0)}
                        onApplyTemplate={applyTemplateId}
                        templateSourceCount={templateSourceCount}
                        onAddColor={addColorCard}
                    />
                </div>
                <PreviewStage
                    comp={comp}
                    playback={playback}
                    history={history}
                    selection={selection}
                    onSelect={setSelection}
                    onAspect={changeAspect}
                    safeArea={safeArea}
                    onToggleSafeArea={() => setSafeArea((s) => !s)}
                />
                <Inspector comp={comp} history={history} selection={selection} brand={brand} />
            </div>

            {/* Timeline */}
            <div className="h-[290px] shrink-0">
                <Timeline
                    comp={comp}
                    history={history}
                    time={time}
                    playing={playback.playing}
                    onSeek={(tt) => playback.seek(tt)}
                    selection={selection}
                    onSelect={setSelection}
                    pxPerSec={pps}
                    onZoom={setPps}
                    snapping={snapping}
                    onToggleSnapping={() => setSnapping((s) => !s)}
                    onSplit={splitAtPlayhead}
                    onDelete={deleteSelection}
                    onDuplicate={duplicateSelection}
                    onDropMedia={onDropMedia}
                />
            </div>

            {exportState && (
                <ExportOverlay state={exportState} onCancel={onCancelExport} onDismiss={onDismissExport} onClose={() => onClose(comp)} />
            )}
        </div>
    );
};

const ExportOverlay: React.FC<{
    state: VideoExportState;
    onCancel?: () => void;
    onDismiss?: () => void;
    onClose: () => void;
}> = ({ state, onCancel, onDismiss, onClose }) => {
    const { t } = useTranslation();
    const pct = Math.round((state.progress?.progress ?? 0) * 100);
    const phase = state.progress?.phase ?? 'prepare';
    return (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/70 backdrop-blur-[2px]" data-ve-export-overlay={state.status}>
            <div className="flex w-[380px] flex-col gap-4 rounded-xl border bg-background p-6">
                {state.status === 'running' && (
                    <>
                        <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-foreground">{t('VIDEO_EDITOR.EXPORTING')}</p>
                            <span className="text-xs tabular-nums text-muted-foreground">{pct}%</span>
                        </div>
                        <Progress value={pct} />
                        <p className="text-xs text-muted-foreground">
                            {t(`VIDEO_EDITOR.PHASE.${phase.toUpperCase()}`)}
                            {state.progress?.frame && state.progress.frames
                                ? ` · ${t('VIDEO_EDITOR.FRAME_OF', { frame: state.progress.frame, frames: state.progress.frames })}`
                                : ''}
                        </p>
                        <p className="text-[11px] text-muted-foreground">{t('VIDEO_EDITOR.EXPORT_BACKGROUND_HINT')}</p>
                        <div className="flex justify-end gap-2">
                            <Button type="button" variant="outline" onClick={onClose}>{t('VIDEO_EDITOR.BACK_TO_CANVAS')}</Button>
                            <Button type="button" variant="outline" className="border-destructive text-destructive hover:bg-destructive/5 hover:text-destructive" onClick={onCancel}>
                                {t('VIDEO_EDITOR.CANCEL_EXPORT')}
                            </Button>
                        </div>
                    </>
                )}
                {state.status === 'done' && (
                    <>
                        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                            <span className="flex size-6 items-center justify-center rounded-full border"><Check className="size-3.5" /></span>
                            {t('VIDEO_EDITOR.EXPORT_DONE')}
                        </div>
                        <p className="text-xs text-muted-foreground">{t('VIDEO_EDITOR.EXPORT_DONE_HINT')}</p>
                        <div className="flex justify-end gap-2">
                            {state.url && (
                                <Button type="button" variant="outline" asChild>
                                    <a href={state.url} target="_blank" rel="noreferrer" download>
                                        <Download />
                                        {t('VIDEO_EDITOR.DOWNLOAD')}
                                    </a>
                                </Button>
                            )}
                            <Button type="button" variant="outline" onClick={onDismiss}>{t('VIDEO_EDITOR.KEEP_EDITING')}</Button>
                            <Button type="button" onClick={onClose}>{t('VIDEO_EDITOR.BACK_TO_CANVAS')}</Button>
                        </div>
                    </>
                )}
                {state.status === 'error' && (
                    <>
                        <p className="text-sm font-medium text-foreground">{t('VIDEO_EDITOR.EXPORT_FAILED')}</p>
                        <p className="text-xs text-muted-foreground">{state.error || t('VIDEO_EDITOR.EXPORT_FAILED_HINT')}</p>
                        <div className="flex justify-end">
                            <Button type="button" variant="outline" onClick={onDismiss}>{t('VIDEO_EDITOR.KEEP_EDITING')}</Button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
